import type { Locale } from "../config/langs";

// Tag dictionary — maps the canonical English tag (as stored in the sheet)
// to localized labels. Falls back to the raw tag if missing.
export const TAG_I18N: Record<string, Record<Locale, string>> = {
  verb: { fr: "verbe", bg: "глагол", en: "verb" },
  noun: { fr: "nom", bg: "съществително", en: "noun" },
  adjective: { fr: "adjectif", bg: "прилагателно", en: "adjective" },
  adverb: { fr: "adverbe", bg: "наречие", en: "adverb" },
  phrase: { fr: "phrase", bg: "израз", en: "phrase" },
  idiom: { fr: "idiome", bg: "идиом", en: "idiom" },
  question: { fr: "question", bg: "въпрос", en: "question" },
  imperfective: { fr: "imperfectif", bg: "несвършен", en: "imperfective" },
  perfective: { fr: "perfectif", bg: "свършен", en: "perfective" },
  reflexive: { fr: "réfléchi", bg: "възвратен", en: "reflexive" },
  modal: { fr: "modal", bg: "модален", en: "modal" },
  abstract: { fr: "abstrait", bg: "абстрактно", en: "abstract" },
  perception: { fr: "perception", bg: "възприятие", en: "perception" },
  mind: { fr: "esprit", bg: "ум", en: "mind" },
  communication: { fr: "communication", bg: "общуване", en: "communication" },
  emotion: { fr: "émotion", bg: "емоция", en: "emotion" },
  motion: { fr: "mouvement", bg: "движение", en: "motion" },
  food: { fr: "nourriture", bg: "храна", en: "food" },
  drink: { fr: "boisson", bg: "напитка", en: "drink" },
  body: { fr: "corps", bg: "тяло", en: "body" },
  organ: { fr: "organe", bg: "орган", en: "organ" },
  family: { fr: "famille", bg: "семейство", en: "family" },
  social: { fr: "social", bg: "социален", en: "social" },
  work: { fr: "travail", bg: "работа", en: "work" },
  education: { fr: "éducation", bg: "образование", en: "education" },
  character: { fr: "caractère", bg: "характер", en: "character" },
  weather: { fr: "météo", bg: "време", en: "weather" },
  nature: { fr: "nature", bg: "природа", en: "nature" },
  time: { fr: "temps", bg: "време", en: "time" },
  day: { fr: "jour", bg: "ден", en: "day" },
  month: { fr: "mois", bg: "месец", en: "month" },
  frequency: { fr: "fréquence", bg: "честота", en: "frequency" },
  greeting: { fr: "salutation", bg: "поздрав", en: "greeting" },
  money: { fr: "argent", bg: "пари", en: "money" },
  culture: { fr: "culture", bg: "култура", en: "culture" },
  // Broad domain buckets added for the filter popover's domain axis.
  // `mind` already exists above (line 19), kept as-is.
  society: { fr: "société", bg: "общество", en: "society" },
  action: { fr: "action", bg: "действие", en: "action" },
  object: { fr: "objet", bg: "предмет", en: "object" },
  // Register axis.
  formal: { fr: "soutenu", bg: "официален", en: "formal" },
  informal: { fr: "familier", bg: "неофициален", en: "informal" },
  slang: { fr: "argot", bg: "жаргон", en: "slang" },
  // Capitales (world continents) — raw tags stored in the CSV.
  europe: { fr: "Europe", bg: "Европа", en: "Europe" },
  asie: { fr: "Asie", bg: "Азия", en: "Asia" },
  afrique: { fr: "Afrique", bg: "Африка", en: "Africa" },
  oceanie: { fr: "Océanie", bg: "Океания", en: "Oceania" },
  "amerique-nord": { fr: "Amérique du Nord", bg: "Северна Америка", en: "North America" },
  "amerique-sud": { fr: "Amérique du Sud", bg: "Южна Америка", en: "South America" },
  // French départements — 13 régions + outre-mer.
  "auvergne-rhone-alpes": { fr: "Auvergne-Rhône-Alpes", bg: "Оверн-Рона-Алпи", en: "Auvergne-Rhône-Alpes" },
  "hauts-de-france": { fr: "Hauts-de-France", bg: "О дьо Франс", en: "Hauts-de-France" },
  "grand-est": { fr: "Grand Est", bg: "Гранд Ест", en: "Grand Est" },
  "provence-alpes-cote-dazur": { fr: "Provence-Alpes-Côte d'Azur", bg: "Прованс-Алпи-Лазурен бряг", en: "Provence-Alpes-Côte d'Azur" },
  normandie: { fr: "Normandie", bg: "Нормандия", en: "Normandy" },
  occitanie: { fr: "Occitanie", bg: "Окситания", en: "Occitania" },
  "nouvelle-aquitaine": { fr: "Nouvelle-Aquitaine", bg: "Нова Аквитания", en: "Nouvelle-Aquitaine" },
  "centre-val-de-loire": { fr: "Centre-Val de Loire", bg: "Център-Вал дьо Лоар", en: "Centre-Val de Loire" },
  bretagne: { fr: "Bretagne", bg: "Бретан", en: "Brittany" },
  "pays-de-la-loire": { fr: "Pays de la Loire", bg: "Пеи дьо ла Лоар", en: "Pays de la Loire" },
  "bourgogne-franche-comte": { fr: "Bourgogne-Franche-Comté", bg: "Бургундия-Франш-Конте", en: "Bourgogne-Franche-Comté" },
  "ile-de-france": { fr: "Île-de-France", bg: "Ил дьо Франс", en: "Île-de-France" },
  corse: { fr: "Corse", bg: "Корсика", en: "Corsica" },
  "outre-mer": { fr: "Outre-mer", bg: "Отвъдморски територии", en: "Overseas" },
  // US states — 4 census regions.
  "nord-est": { fr: "Nord-Est", bg: "Североизток", en: "Northeast" },
  sud: { fr: "Sud", bg: "Юг", en: "South" },
  midwest: { fr: "Midwest", bg: "Среден запад", en: "Midwest" },
  ouest: { fr: "Ouest", bg: "Запад", en: "West" },
};

export function translateTag(tag: string, locale: Locale): string {
  const key = String(tag || "").trim().toLowerCase();
  const entry = TAG_I18N[key];
  if (entry) return entry[locale] || entry.en || tag;
  return tag;
}
